import React, { useEffect, useMemo, useState } from 'react';
/**
 * ScheduleRenderer — Interactive timetable grid with cell selection.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s68.
 */
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { Clock } from 'lucide-react';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
export function ScheduleRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  const schedule = config.schedule || {
    columns: [],
    timeSlots: [],
    events: []
  };
  const [selectedCell, setSelectedCell] = useState<{
    col: number;
    row: number;
  } | null>(null);
  useEffect(() => {
    setSelectedCell(null);
  }, [question.id]);
  useEffect(() => {
    onReadyChange?.(selectedCell !== null);
  }, [selectedCell, onReadyChange]);
  const answerPayload = useMemo(() => selectedCell ? `${selectedCell.row}-${selectedCell.col}` : '', [selectedCell]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  const handleCellClick = (row: number, col: number) => {
    setSelectedCell(selectedCell?.row === row && selectedCell?.col === col ? null : {
      row,
      col
    });
  };
  const handleKeyDown = (e: React.KeyboardEvent, row: number, col: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCellClick(row, col);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      document.getElementById(`schedule-cell-${question.id}-${row}-${(col + 1) % schedule.columns.length}`)?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      document.getElementById(`schedule-cell-${question.id}-${row}-${(col - 1 + schedule.columns.length) % schedule.columns.length}`)?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      document.getElementById(`schedule-cell-${question.id}-${(row + 1) % schedule.timeSlots.length}-${col}`)?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      document.getElementById(`schedule-cell-${question.id}-${(row - 1 + schedule.timeSlots.length) % schedule.timeSlots.length}-${col}`)?.focus();
    }
  };
  const getEvent = (row: number, col: number) => schedule.events.find((e: {
    row: number;
    col: number;
  }) => e.row === row && e.col === col);
  const eventColorClass = (color?: string) => {
    if (color === 'blue') return 'bg-blue-100 text-blue-800';
    if (color === 'green') return 'bg-emerald-100 text-emerald-800';
    if (color === 'orange') return 'bg-orange-100 text-orange-800';
    if (color === 'red') return 'bg-rose-100 text-rose-800';
    return 'bg-slate-100 text-slate-700';
  };
  return <div className="w-full max-w-4xl mx-auto overflow-hidden rounded-xl border border-slate-200 shadow-sm bg-white" data-id="element-10935">
      <div className="overflow-x-auto" data-id="element-10936">
        <table className="w-full border-collapse min-w-[600px]" role="grid" aria-label="Rooster" data-id="element-10937">
          <thead className="sticky top-0 z-20" data-id="element-10938">
            <tr className="bg-slate-50 border-b border-slate-200" role="row" data-id="element-10939">
              <th className="p-3 w-20 border-r border-slate-200 bg-slate-100 sticky left-0 z-30" role="columnheader" data-id="element-10940">
                <Clock className="w-5 h-5 text-slate-400 mx-auto" aria-hidden="true" data-id="element-10941" />
              </th>
              {schedule.columns.map((col: string, i: number) => <th key={i} role="columnheader" className="p-3 text-sm font-bold text-slate-700 text-center border-r border-slate-200 last:border-0 min-w-[100px] bg-slate-50" data-id="element-10942">
                  <LatexRenderer content={col} data-id="element-10943" />
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-10944">
            {schedule.timeSlots.map((time: string, rowIndex: number) => <tr key={rowIndex} role="row" className="border-b border-slate-100 last:border-0" data-id="element-10945">
                <td role="rowheader" className="p-3 text-xs font-bold text-slate-500 text-center border-r border-slate-200 bg-slate-50/30 sticky left-0 z-10" data-id="element-10946">
                  {time}
                </td>
                {schedule.columns.map((_: string, colIndex: number) => {
              const event = getEvent(rowIndex, colIndex);
              const isSelected = selectedCell?.row === rowIndex && selectedCell?.col === colIndex;
              return <td key={colIndex} id={`schedule-cell-${question.id}-${rowIndex}-${colIndex}`} role="gridcell" aria-selected={isSelected} tabIndex={0} onClick={() => handleCellClick(rowIndex, colIndex)} onKeyDown={e => handleKeyDown(e, rowIndex, colIndex)} className={`p-2 border-r border-slate-100 last:border-0 cursor-pointer transition-all relative h-16 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 ${isSelected ? 'bg-indigo-50 ring-2 ring-inset ring-indigo-500 z-0' : 'hover:bg-slate-50'}`} data-id="element-10947">
                      {event ? <div className={`w-full h-full rounded-lg p-2 text-xs font-medium leading-tight flex items-center justify-center text-center ${eventColorClass(event.color)}`} data-id="element-10948">
                          <LatexRenderer content={event.content} data-id="element-10949" />
                        </div> : <div className="w-full h-full" data-id="element-10950" />}
                    </td>;
            })}
              </tr>)}
          </tbody>
        </table>
      </div>
      <div className="bg-slate-50 p-3 text-center text-sm text-slate-500 border-t border-slate-200" data-id="element-10951">
        Klik op een vakje of gebruik de pijltjestoetsen
      </div>
    </div>;
}